import { LitElement, html, nothing } from 'lit';
import { RefiAnalytics, trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { displayProgressBarEvent, updateMacroStepEvent, updateMicroStepEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar.js';
import { DebugService } from './shared/services/debug.service.js';
import { SessionService } from './core/services/session/session.service.js';
import { AppService } from './app.service.js';
import { REFI_OFFERS_CONST } from './shared/constants/app.constants.js';
import { stratum } from '../constants/stratum.js';
import { ExperimentService } from './shared/services/experiment.service.js';
import { FeatureToggleService } from './shared/services/feature-toggle.service.js';
import { OffersService } from './offers/offers.service.js';
import './offers/offers.js';
import { CookieService } from './shared/services/cookie.service.js';
import '@cof/fs-button-wc/fs-button.js';

class RefiApplyApp extends LitElement {
  constructor() {
    super();
    this.loadingApp = true;
    this.debug = new DebugService('RefiApplyApp');
    this.featureTogglesParam = new URL(window.location.href).searchParams.get('feature-toggle');
    if (this.featureTogglesParam && !REFI_OFFERS_CONST.isProdEnv) {
      this.featureToggles = JSON.parse(this.featureTogglesParam);
    }
    this.experimentParam = new URL(window.location.href).searchParams.get('experiment');
    if (this.experimentParam && !REFI_OFFERS_CONST.isProdEnv) {
      this.experiments = {
        Iconic: {
          allocation: this.experimentParam,
          isEligible: true,
        },
      };
    }
    this.cypressParam = new URL(window.location.href).searchParams.get('cypress');
    this.testingParam = new URL(window.location.href).searchParams.get('testing');
    if ((this.testingParam || this.cypressParam) && !REFI_OFFERS_CONST.isProdEnv) {
      this.session = SessionService.mockSession;
      OffersService.isReviewOfferCardTesting = true;
    } else {
      this.session = SessionService.session || {};
    }
    this.offersType = REFI_OFFERS_CONST.OFFERS_TYPE.postApp;

    this._state = {
      appStatus: this.session.currentApp?.appStatusId,
      statFlag: this.session.currentApp?.statFlag,
    };

    this.onProcessingPage = false;
    this.baseHref = document.querySelector('base').getAttribute('href');
    this.hasStips = false;
    this.addEventListener(REFI_OFFERS_CONST.ROUTE_CHANGE, () => {
      this.requestUpdate();
    });
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    this.updateStratumMarketingChannel();
    this.analytics = new RefiAnalytics('apply', ['sitecatalyst', 'potomac'], appId);
    this.isIconic = false;
    this.existingExperimentIds = [];
  }

  static properties = {
    hasStips: { type: Boolean },
    loadingApp: { type: Boolean },
    onProcessingPage: { type: Boolean },
    canHelp: { type: Boolean },
    status: { type: String },
    featureToggles: { type: Object, attribute: 'feature-toggles' },
    offersType: { type: String, attribute: 'offers-type' },
    state: { type: Object, attribute: true },
    checkout: { type: Boolean, attribute: 'checkout-page' },
    currentStep: { type: String, attribute: 'current-step', reflect: true },
    v2Routing: {
      type: Boolean,
      attribute: 'v2-routing',
      reflect: true,
      hasChanged: newVal => {
        AppService.isV2Routing = newVal;
      },
    },
    experiments: {
      type: Object,
      hasChanged: (newVal, oldVal) => {
        if (newVal !== oldVal) {
          // loop through this.experiments and add the object name to experimentIds array if it's eligible
          const experimentIds = [];
          let removeExperimentType = '';
          Object.keys(newVal).forEach(experiment => {
            if (newVal[experiment].isEligible) {
              experimentIds.push(newVal[experiment].allocation);
            } else {
              experimentIds.push(`${experiment}-ineligible`);
              removeExperimentType = `${experiment}`;
            }
          });
          ExperimentService.updateExperiments(experimentIds, this.existingExperimentIds, removeExperimentType);
          this.existingExperimentIds = experimentIds;
          return true;
        }
        return false;
      },
    },
  };

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    FeatureToggleService.setFeatureToggles(this.featureToggles);
    ExperimentService.updateStratumExperiments(this.featureToggles);
    document.addEventListener('sign-out', this.signOut.bind(this));

    if (this.testingParam && !REFI_OFFERS_CONST.isProdEnv) {
      this.session = SessionService.mockSession;
      OffersService.isReviewOfferCardTesting = true;
    } else {
      OffersService.refreshSession();
      this.session = SessionService.session || {};
    }
    let appStatusId;
    let statFlag;
    if (this.session.currentApp) {
      appStatusId = this.session.currentApp.appStatusId;
      statFlag = this.session.currentApp.statFlag;
      this._state = {
        appStatus: appStatusId,
        statFlag,
      };
      AppService.updateState({ appStatus: appStatusId, statFlag });
    }
    this.loadOffersData();
    displayProgressBarEvent(true);
    updateMacroStepEvent('Lock in payment', ['Offers', 'Review', 'Contract']);
    updateMicroStepEvent('');
  }

  render() {
    this.debug.log(`rendered: ${window.location.href} `);
    if (AppService.isV2Routing) {
      if (this.loadingApp && !this.checkout) {
        return html`<refi-loading-spinner message="Loading your offers" class="_gb-show"></refi-loading-spinner>`;
      }
      if (this.currentStep === 'review-offer' || this.checkout) {
        return html`<refi-review-offer-card></refi-review-offer-card>`;
      }
      if (this.currentStep === 'offers') {
        return html`<refi-iconic-choose-offer></refi-iconic-choose-offer>`;
      }
      return nothing;
    }
    const baseUrl = '#/offers/';
    return html`
      <div>
        ${(!this.loadingApp && window.location.href.includes(`review-offer`)) || this.checkout ? html`<refi-review-offer-card></refi-review-offer-card>` : ''}
        ${window.location.href.includes(`${baseUrl}offers`)
          ? !this.loadingApp
            ? html`<lit-route component="refi-iconic-choose-offer" .resolve="${() => this.checkState('offers').then(() => import('./offers/offers.js').catch(() => this.signOut()))}" loading="refi-loading-spinner" class="_gb-show"></lit-route>`
            : html`<refi-loading-spinner message="Getting your offers" class="_gb-show"></refi-loading-spinner>`
          : nothing}
        <refi-common-app-timeout timer="840000" timeout-url="/login" clear-session="auto-refi-session"></refi-common-app-timeout>
      </div>
    `;
  }

  loadOffersData() {
    document.addEventListener('sign-out', this.signOut.bind(this));
    if (this.session?.currentApp?.appStatusId !== 402) {
      this.debug.log('beginning to load app');
      this.loadingApp = true;
      this.debug.log('isNotPfci: ', !SessionService.pfci);
      if (this.testingParam && !REFI_OFFERS_CONST.isProdEnv) {
        OffersService.isReviewOfferCardTesting = true;
        AppService.getMockApplicationInfo().then(res => {
          SessionService.hasCoBorrower(res.hasCoBorrower);
          this.debug.log('getAppInfo successful', res);
          this.debug.log('clear loading app flag');
          this.loadingApp = false;
          AppService.updateState({});
          this.hasStips = AppService.hasStips;
        });
      } else if (AppService.session?.eAppId) {
        AppService.getApplicationInfo().then(
          res => {
            SessionService.hasCoBorrower(res.hasCoBorrower);
            this.debug.log('getAppInfo successful', res);
            this.debug.log('clear loading app flag');
            this.loadingApp = false;
            AppService.updateState({});
            this.hasStips = AppService.hasStips;
          },
          err => {
            this.debug.log('getAppInfo failed', err);
            // if (!window.location.pathname.includes('login')) {
            //   this.signOut();
            // }
          },
        );
      }
    } else {
      this.debug.log('Unable to obtain app info with available information');
      AppService.updateState({});
    }
    this.checkReturnExperienceId();
  }

  disconnectedCallback() {
    document.removeEventListener('sign-out', this.signOut);
    super.disconnectedCallback();
  }

  checkReturnExperienceId() {
    const returnIdState = sessionStorage.getItem('returnIdExperience');
    if (returnIdState) {
      trackAnalyticsEvent(`returnIdExperience-${returnIdState}`);
    }
  }

  checkState(shouldBe, showHeader = true) {
    this.status = AppService.status.toLowerCase();
    this.debug.log(`Calling checkState: status:${this.status} || shouldbe:${shouldBe}`);
    return new Promise((res, rej) => {
      if (this.status.toLowerCase() === shouldBe.toLowerCase()) {
        this.onProcessingPage = !showHeader;
        this.canHelp = this.status.toLowerCase() !== 'invalid-offer';
        res();
      } else {
        AppService.updateState({});
        rej();
      }
    });
  }

  signOut() {
    // SessionService.removeSession();
    // if (REFI_APPLY_CONST.isQAEnv() || REFI_APPLY_CONST.isProdEnv()) {
    //   const idpLogoutUrl = REFI_APPLY_CONST.IDP_DXGW_LOGOUT_URL();
    //   window.open(idpLogoutUrl, '_self');
    // } else {
    //   window.open('/login', '_self');
    // }
    this.debug.log('Send back to login.');
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(REFI_OFFERS_CONST.MARKETING_ATTRIBUTE_COOKIE)?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar('marketingChannel', externalIdFromCookie ?? SessionService.getExternalId());
    }
  }
}

customElements.define('refi-offers-app', RefiApplyApp);
